import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import BalanceStateRepository from "../../Repositories/BalanceStateRepository";

export interface BalanceStateResponse {
  id: string;
  previousBalanceId?: string;
  locationId: string;
  newBalance: number;
  createdBy: string;
  createdOn: Dayjs;
}

interface YearlyBalanceChartProps {
  height?: number;
  includeZero?: boolean;
  style?: React.CSSProperties;
  lineColor?: string;
  areaTop?: string;
  areaBottom?: string;
  gridColor?: string;
  frameColor?: string;
}

interface YearlyBalanceChartStates {
  width: number;
  months: Dayjs[];
  values: number[];
}

export default class YearlyBalanceChart extends React.Component<
  YearlyBalanceChartProps,
  YearlyBalanceChartStates
> {
  static contextType = AuthContext;
  context!: AuthContextType;

  private accent = "#c79656";

  private defaultProps = {
    height: 180,
    includeZero: true,
    lineColor: this.accent,
    areaTop: this.accent,
    areaBottom: this.accent,
    gridColor: "#e5edf6",
    frameColor: "#e5edf6",
  };

  private containerRef = React.createRef<HTMLDivElement>();
  private clipId = `clip-yearly-balance-${Math.random().toString(36).slice(2)}`;

  constructor(props: any) {
    super(props);

    this.getBalanceOverview = this.getBalanceOverview.bind(this);

    this.state = {
      width: 640,
      months: [],
      values: [],
    };
  }

  public componentDidMount(): void {
    this.measure();
    this.getBalanceOverview();
    window.addEventListener("resize", this.measure);
  }

  public componentWillUnmount(): void {
    window.removeEventListener("resize", this.measure);
  }

  private async getBalanceOverview() {
    const startMonth = dayjs().startOf("month").subtract(6, "month");
    const endMonth = dayjs().startOf("month");

    const result = await BalanceStateRepository.getAllBalanceStates(
      this.context.locationId,
      undefined,
      startMonth,
      endMonth
    );
    if (result.isFailed()) return;

    const states = (result.result!.balanceStates as BalanceStateResponse[])
      .slice()
      .sort(
        (a, b) => dayjs(a.createdOn).valueOf() - dayjs(b.createdOn).valueOf()
      );

    const months = Array.from({ length: 7 }, (_, i) => startMonth.add(i, "month"));

    const monthKey = (d: Dayjs) => d.format("YYYY-MM");

    const latestByMonth = new Map<string, BalanceStateResponse>();
    for (const s of states) {
      const k = monthKey(dayjs(s.createdOn).startOf("month"));
      latestByMonth.set(k, s);
    }

    const values: number[] = [];
    let last: number | null = null;

    for (const m of months) {
      const hit = latestByMonth.get(monthKey(m));
      if (hit) last = hit.newBalance;
      values.push(last ?? 0);
    }

    this.setState({ months, values });
  }

  private measure = () => {
    const width = this.containerRef.current?.offsetWidth;
    if (width && Math.abs(width - this.state.width) > 8) {
      this.setState({ width: width });
    }
  };

  private chartNumber = (range: number, round: boolean) => {
    const exp = Math.floor(Math.log10(range));
    const frac = range / Math.pow(10, exp);
    let niceFrac: number;

    if (round) {
      if (frac < 1.5) niceFrac = 1;
      else if (frac < 3) niceFrac = 2;
      else if (frac < 3.75) niceFrac = 2.5;
      else if (frac < 7.5) niceFrac = 5;
      else niceFrac = 10;
    } else {
      if (frac <= 1) niceFrac = 1;
      else if (frac <= 2) niceFrac = 2;
      else if (frac <= 2.5) niceFrac = 2.5;
      else if (frac <= 5) niceFrac = 5;
      else niceFrac = 10;
    }

    return niceFrac * Math.pow(10, exp);
  };

  private chartTicks = (min: number, max: number, maxTicks: number) => {
    const rawRange = Math.max(1e-9, max - min);
    const step = this.chartNumber(rawRange / Math.max(1, maxTicks - 1), true);
    const niceMin = Math.floor(min / step) * step;
    const niceMax = Math.ceil(max / step) * step;

    const ticks: number[] = [];
    for (let v = niceMin; v <= niceMax + 1e-9; v += step) {
      ticks.push(Number(v.toFixed(12)));
    }

    return { niceMin, niceMax, step, ticks };
  };

  private chartEuro = (v: number) => {
    const abs = Math.abs(v);

    const formatNumber = (num: number) => {
      const rounded = Math.round(num * 100) / 100;
      return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
    };

    if (abs >= 1_000_000) return `€${formatNumber(v / 1_000_000)}M`;
    if (abs >= 1_000) return `€${formatNumber(v / 1_000)}k`;
    return `€${formatNumber(v)}`;
  };

  private createChartArea(
    values: number[],
    width: number,
    height: number,
    opts?: { includeZero?: boolean; padX?: number }
  ) {
    const includeZero = opts?.includeZero ?? true;
    const padX = opts?.padX ?? 40;
    const padR = 12;
    const padT = 12;
    const padB = 14;

    const w = Math.max(1, width - padX - padR);
    const h = Math.max(1, height - padT - padB);

    if (!values?.length) {
      const sx = (_i: number) => padX + w / 2;
      const sy = (_v: number) => padT + h;

      return {
        pathD: "",
        areaD: "",
        yTicks: [] as number[],
        yLabels: [] as string[],
        padX,
        padT,
        w,
        h,
        sx,
        sy,
        niceMin: 0,
        niceMax: 1,
      };
    }

    let minValue = Math.min(...values);
    let maxValue = Math.max(...values);

    if (minValue === maxValue) {
      const spread = Math.max(1, Math.abs(maxValue) * 0.1);
      minValue -= spread;
      maxValue += spread;
    }

    const pad = (maxValue - minValue) * 0.06;
    minValue -= pad;
    maxValue += pad;

    if (includeZero) {
      if (minValue > 0) minValue = 0;
      if (maxValue < 0) maxValue = 0;
    }

    const desired = Math.min(8, Math.max(3, Math.round(h / 42)));
    const { niceMin, niceMax, ticks } = this.chartTicks(minValue, maxValue, desired);

    const sx = (i: number) =>
      (values.length === 1 ? w / 2 : (i / (values.length - 1)) * w) + padX;

    const sy = (v: number) => {
      const range = niceMax - niceMin || 1;
      const t = (v - niceMin) / range;
      return padT + (1 - t) * h;
    };

    const pts = values.map((v, i) => [sx(i), sy(v)] as const);

    const pathD = pts
      .map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`))
      .join(" ");

    const areaD =
      `M ${padX},${sy(niceMin)} ` +
      pts.map((p) => `L ${p[0]},${p[1]}`).join(" ") +
      ` L ${padX + w},${sy(niceMin)} Z`;

    const yTicks = ticks.map((t) => sy(t));
    const yLabels = ticks.map((t) => this.chartEuro(t));

    return {
      pathD,
      areaD,
      yTicks,
      yLabels,
      padX,
      padT,
      w,
      h,
      sx,
      sy,
      niceMin,
      niceMax,
    };
  }

  public render() {
    const height = this.props.height ?? this.defaultProps.height;
    const includeZero = this.props.includeZero ?? this.defaultProps.includeZero;

    const yLine = this.createChartArea(this.state.values, this.state.width, height, {
      includeZero,
    });

    const xLeft = yLine.padX;
    const xRight = yLine.padX + yLine.w;

    const approxLabelW = 36;
    const maxLabels = Math.max(1, Math.floor(yLine.w / approxLabelW));
    const n = this.state.months.length || this.state.values.length || 7;
    const step = Math.max(1, Math.ceil(n / Math.max(1, maxLabels)));

    const lineColor = this.props.lineColor ?? this.defaultProps.lineColor;
    const areaTop = this.props.areaTop ?? this.defaultProps.areaTop;
    const areaBottom = this.props.areaBottom ?? this.defaultProps.areaBottom;
    const gridColor = this.props.gridColor ?? this.defaultProps.gridColor;
    const frameColor = this.props.frameColor ?? this.defaultProps.frameColor;

    const monthsForLabels =
      this.state.months.length
        ? this.state.months
        : Array.from({ length: n }, (_, i) =>
            dayjs().startOf("month").subtract(n - 1, "month").add(i, "month")
          );

    return (
      <div ref={this.containerRef} style={{ width: "100%", ...this.props.style }}>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${this.state.width} ${height}`}
          role="img"
          style={{ display: "block" }}
        >
          <defs>
            <clipPath id={this.clipId}>
              <rect x={xLeft} y={12} width={yLine.w} height={height - 12 - 14} rx={10} ry={10} />
            </clipPath>

            <linearGradient id="grad-jarArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaTop} stopOpacity="0.35" />
              <stop offset="100%" stopColor={areaBottom} stopOpacity="0.08" />
            </linearGradient>
          </defs>

          <rect
            x={xLeft}
            y={12}
            width={yLine.w}
            height={height - 12 - 14}
            rx={10}
            ry={10}
            fill="none"
            stroke={frameColor}
          />

          {yLine.yTicks.map((yy: number, i: number) => (
            <line
              key={i}
              x1={xLeft}
              y1={yy}
              x2={xRight}
              y2={yy}
              stroke={gridColor}
              strokeDasharray="4 6"
            />
          ))}

          <g clipPath={`url(#${this.clipId})`}>
            <path d={yLine.areaD} fill="url(#grad-jarArea)" />
            <path
              d={yLine.pathD}
              fill="none"
              stroke={lineColor}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {this.state.values.length > 0 && (
              <circle
                cx={yLine.sx(this.state.values.length - 1)}
                cy={yLine.sy(this.state.values[this.state.values.length - 1])}
                r={3.5}
                fill={lineColor}
                stroke="#fff"
                strokeWidth={1.5}
              />
            )}
          </g>

          {monthsForLabels.map((m, i) => {
            if (i % step !== 0) return null;
            const x = yLine.sx(i);
            return (
              <text key={i} x={x} y={height - 2} textAnchor="middle" fontSize="11" fill="#334155">
                {m.format("MMM")}
              </text>
            );
          })}

          {yLine.yTicks.map((yy: number, i: number) => (
            <text key={`y${i}`} x={xLeft - 8} y={yy + 4} textAnchor="end" fontSize="11" fill="#64748b">
              {yLine.yLabels[i]}
            </text>
          ))}
        </svg>
      </div>
    );
  }
}
